import { useState } from 'react';

import { fak } from '@awesome.me/kit-da59d5031a/icons';
import { faArrowLeft, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { RadioGroupOptions, RadioGroupVariants } from '@sage/ui-components';

import { useAnalytics } from 'src/AnalyticsContext';
import DrawerBody from 'src/components/drawers/DrawerBody';
import DrawerFooter from 'src/components/drawers/DrawerFooter';
import DrawerTitle from 'src/components/drawers/DrawerTitle';
import AssistanceLevelDescriptions from 'src/components/drawers/SectionGgResolutionFlow/AssistanceLevelDescriptions';
import LargeIconSelectButton from 'src/components/fragments/LargeIconSelectButton';
import {
    AssistanceLevelOptions,
    HelperLevelOptions,
    IdToSkilledNursingAdlOptionMap,
} from 'src/constants/SectionGgResolutionTypes';
import { AssistanceLevel } from 'src/store/sageCareServiceApi';

interface AssistanceLevelDrawerProps {
    selectedAdlId: string;
    onSelect: (level: AssistanceLevel, helperLevel?: string) => void;
    onClose: () => void;
}

const helperLevelOptions = HelperLevelOptions.map((option) => ({
    label: (
        <Typography variant="body1" sx={{ flex: 1 }}>
            {option.description} <b>{option.displayName}</b>
        </Typography>
    ),
    value: option.id,
}));

function AssistanceLevelDrawer(props: AssistanceLevelDrawerProps) {
    const { selectedAdlId, onSelect, onClose } = props;

    const { palette } = useTheme();
    const { trackEvent } = useAnalytics();

    const [selectedLevel, setSelectedLevel] = useState<AssistanceLevel | undefined>(undefined);
    const [showHelperLevelScreen, setShowHelperLevelScreen] = useState(false);
    const [selectedHelperLevelId, setSelectedHelperLevelId] = useState<string | undefined>(
        undefined
    );

    const adlDisplayName = IdToSkilledNursingAdlOptionMap.get(selectedAdlId)?.displayName ?? '';

    const handleSelectAssistanceLevel = (level: AssistanceLevel) => {
        trackEvent('Assistance Level Option Clicked', { selectedAdlId, selectedLevel: level });

        setSelectedLevel(level);
    };

    const handleSelectHelperLevel = (helperLevelId: string) => {
        trackEvent('Helper Level Option Clicked', {
            selectedAdlId,
            assistanceLevel: selectedLevel,
            selectedHelperLevelId: helperLevelId,
        });

        setSelectedHelperLevelId(helperLevelId);
    };

    const handleConfirmLevelOfAssistance = () => {
        if (!selectedLevel) return;
        trackEvent('Assistance Level Selected', { selectedAdlId, selectedLevel });

        if (selectedLevel === 'DEPENDENT') {
            setShowHelperLevelScreen(true);
        } else {
            onSelect(selectedLevel);
        }
    };

    const handleConfirmHelperLevel = () => {
        trackEvent('Helper Level Selected', {
            selectedAdlId,
            selectedLevel,
            selectedHelperLevelId,
        });

        if (selectedHelperLevelId) {
            onSelect('DEPENDENT', selectedHelperLevelId);
        }
    };

    const handleBackFromHelperLevel = () => {
        trackEvent('Back To Assistance Level Clicked', { selectedAdlId, selectedLevel });

        setShowHelperLevelScreen(false);
        setSelectedHelperLevelId(undefined);
    };

    return (
        <>
            <DrawerTitle sx={{ gap: 0 }}>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr auto',
                        alignItems: 'center',
                    }}
                >
                    <Box sx={{ width: 40 }}>
                        {showHelperLevelScreen && (
                            <IconButton
                                aria-label="back"
                                onClick={handleBackFromHelperLevel}
                                size="medium"
                                disableRipple
                                sx={{ padding: 0 }}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} color={palette.gray?.[900]} />
                            </IconButton>
                        )}
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{ fontWeight: 'bold', textAlign: 'center', minWidth: 0 }}
                    >
                        {showHelperLevelScreen ? 'Dependent' : `${adlDisplayName} Assistance Level`}
                    </Typography>
                    <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            size="medium"
                            disableRipple
                            sx={{ padding: 0 }}
                        >
                            <FontAwesomeIcon icon={faTimes} color={palette.gray?.[900]} />
                        </IconButton>
                    </Box>
                </Box>
            </DrawerTitle>

            <DrawerBody allowShrink>
                <Box sx={{ mb: 1.5 }}>
                    {showHelperLevelScreen ? (
                        <RadioGroupOptions
                            options={helperLevelOptions}
                            selectedOption={selectedHelperLevelId}
                            onSelectOption={handleSelectHelperLevel}
                            variant={RadioGroupVariants.Outlined}
                        />
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns:
                                        'repeat(auto-fill, minmax(min(150px, 100%), 1fr))',
                                    gap: 1.5,
                                }}
                                role="group"
                                aria-label="Select Assistance Level"
                            >
                                {AssistanceLevelOptions.map((option) => (
                                    <LargeIconSelectButton
                                        key={option.id}
                                        id={option.id}
                                        label={option.displayName}
                                        icon={option.icon ?? fak.faIndependent}
                                        iconAlt={option.displayName}
                                        iconSize="4x"
                                        iconColor={palette.purple[700]}
                                        isSelected={selectedLevel === option.id}
                                        onSelect={() => handleSelectAssistanceLevel(option.id)}
                                    />
                                ))}
                            </Box>
                            <AssistanceLevelDescriptions />
                        </>
                    )}
                </Box>
            </DrawerBody>
            <DrawerFooter>
                {showHelperLevelScreen ? (
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        onClick={handleConfirmHelperLevel}
                        disabled={!selectedHelperLevelId}
                    >
                        Select
                    </Button>
                ) : (
                    <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        disableElevation
                        disabled={!selectedLevel}
                        onClick={handleConfirmLevelOfAssistance}
                    >
                        {selectedLevel === 'DEPENDENT' ? 'Next' : 'Select'}
                    </Button>
                )}
            </DrawerFooter>
        </>
    );
}

export default AssistanceLevelDrawer;
